/**
 * IslandRenderer — element renderer for `type: "island"` nodes.
 *
 * Mounts a host-registered React component under an `<IslandRegistry>` name
 * with `resolveSymbol`-evaluated props. Provides the escape hatch for cogo
 * UI trees that need to embed bespoke React state/hooks/providers without
 * reconstructing them in JSON.
 *
 * See .planning/feature-island-design.md for the full design.
 */
import { Suspense } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"

/** Detect expressions the renderer should hand to `resolveSymbol`.
 * Matches v2 renderer convention — strings starting with `#{` or `@{`. */
function isSymbolExpression(value: unknown): value is string {
  if (typeof value !== "string") return false
  return value.startsWith("#{") || value.startsWith("@{")
}

/** Resolve top-level string expressions in a props object. Nested objects /
 * arrays are passed through without recursion — per §4 of the design doc,
 * consumers who need nested expressions should flatten them into top-level
 * keys instead. */
function resolveProps(
  raw: Record<string, unknown>,
  resolve: (expr: string) => unknown,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw)) {
    out[key] = isSymbolExpression(value) ? resolve(value) : value
  }
  return out
}

export function IslandRenderer({ element }: RendererProps) {
  const ctx = useRenderContext()
  const name = typeof element.island === "string" ? element.island : ""
  const rawProps = element.props && typeof element.props === "object" ? element.props : {}
  const fallbackText = typeof element.fallback === "string" ? element.fallback : "Loading…"

  if (!name) {
    console.warn("[cogo-renderer] island node is missing 'island' name", element.id)
    return null
  }

  if (!ctx.islands) {
    console.warn(
      `[cogo-renderer] no IslandRegistry in RenderContext — cannot mount island "${name}" (id=${element.id})`,
    )
    return null
  }

  const Island = ctx.islands.get(name)
  if (!Island) {
    console.warn(
      `[cogo-renderer] unknown island "${name}" (id=${element.id}). ` +
        `Known: [${ctx.islands.list().join(", ")}]`,
    )
    return null
  }

  const resolvedProps = resolveProps(rawProps, ctx.resolveSymbol)

  return (
    <Suspense
      fallback={
        <div
          data-cogo-island-fallback={name}
          className="cogo-island-fallback flex items-center justify-center text-sm text-muted-foreground"
        >
          {fallbackText}
        </div>
      }
    >
      <Island {...resolvedProps} />
    </Suspense>
  )
}
